/**
 * Pure helpers for the continuous-scroll page stack.
 *
 * Page slots reserve exact heights from the stored raster dims recorded at
 * ingest, so the scrollbar is honest and scroll-to-page lands precisely; the
 * IntersectionObserver side feeds `pickMostVisible` to derive the "current"
 * page that the rail highlight, meta strip, and OCR pane follow.
 */

export interface PageGeometry {
    width?: number | null;
    height?: number | null;
}

/** US-letter portrait fallback for legacy pages without recorded dims. */
export const DEFAULT_PAGE_ASPECT = 11 / 8.5;

/** Height/width ratio for a page, falling back to US letter. */
export function pageAspect(page: PageGeometry): number {
    if (
        typeof page.width === 'number' && page.width > 0
        && typeof page.height === 'number' && page.height > 0
    ) {
        return page.height / page.width;
    }
    return DEFAULT_PAGE_ASPECT;
}

/** Reserved slot height (CSS px) for a page rendered at `displayWidth`. */
export function slotHeight(page: PageGeometry, displayWidth: number): number {
    return Math.round(displayWidth * pageAspect(page));
}

/**
 * Choose the page the viewport is "on" from per-page visibility ratios.
 *
 * Sticky on ties (and near-ties) in favor of `currentId` so tiny scroll
 * jitters at page boundaries don't flip the highlight back and forth.
 */
export function pickMostVisible(
    ratios: ReadonlyMap<string, number>,
    currentId: string | null,
    epsilon: number = 0.1,
): string | null {
    let bestId: string | null = null;
    let bestRatio = 0;
    ratios.forEach((ratio, id) => {
        if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
        }
    });
    if (bestId === null) {
        return currentId;
    }
    if (
        currentId !== null
        && currentId !== bestId
        && (ratios.get(currentId) ?? 0) >= bestRatio - epsilon
    ) {
        return currentId;
    }
    return bestId;
}
